// src/pages/ArticlePage.js
// Premium article detail page

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useSpring, useReducedMotion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar, Linkedin } from 'lucide-react';

// Layout components
import SmoothScroll from '../components/SmoothScroll';
import Navbar from '../components/Navbar';
import SEO, { generateArticleSchema } from '../components/SEO';

// Resource components
import RichTextRenderer from '../components/resources/RichTextRenderer';
import RelatedResources from '../components/resources/RelatedResources';

// Hooks
import { useArticle } from '../hooks/useContentful';

// Styles
import './ArticlePage.css';

// Loading skeleton
const ArticlePageSkeleton = () => (
  <div className="article-skeleton">
    <div className="article-skeleton__header">
      <div className="article-skeleton__badge" />
      <div className="article-skeleton__title" />
      <div className="article-skeleton__title article-skeleton__title--short" />
      <div className="article-skeleton__meta" />
    </div>
    <div className="article-skeleton__image" />
    <div className="article-skeleton__body">
      <div className="article-skeleton__line" />
      <div className="article-skeleton__line" />
      <div className="article-skeleton__line article-skeleton__line--short" />
    </div>
  </div>
);

const ArticlePage = () => {
  const { slug } = useParams();
  const prefersReducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Fetch article data
  const { data: article, loading, error } = useArticle(slug);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <SmoothScroll>
        <Navbar />
        <main className="article-page">
          <ArticlePageSkeleton />
        </main>
      </SmoothScroll>
    );
  }

  if (error || !article) {
    return (
      <SmoothScroll>
        <Navbar />
        <main className="article-page">
          <div className="article-page__error">
            <h1>Article not found</h1>
            <p>The article you're looking for doesn't exist or has been moved.</p>
            <Link to="/resources" className="article-page__back-link">
              <ArrowLeft size={16} />
              <span>Back to Resources</span>
            </Link>
          </div>
        </main>
      </SmoothScroll>
    );
  }

  // Generate article schema for SEO
  const articleSchema = generateArticleSchema({
    title: article.title,
    description: article.summary,
    image: article.heroImage?.url,
    author: article.author,
    publishedDate: article.publishedAt,
    url: `/articles/${slug}`,
  });

  return (
    <SmoothScroll>
      {/* SEO */}
      <SEO
        title={article.title}
        description={article.summary || `Read ${article.title} - an article from Rosalytics on ${article.category || 'enterprise technology'}.`}
        canonical={`/articles/${slug}`}
        image={article.heroImage?.url}
        type="article"
        article={{
          author: article.author?.name,
          publishedTime: article.publishedAt,
        }}
        schema={articleSchema}
      />

      {/* Progress Bar */}
      <motion.div className="article-page__progress" style={{ scaleX }} />

      <Navbar />

      <main className="article-page">
        <article className="article-page__article">
          {/* Back link */}
          <div className="article-page__nav">
            <Link to="/resources" className="article-page__back-link">
              <ArrowLeft size={16} />
              <span>Back to Resources</span>
            </Link>
          </div>

          {/* Header */}
          <motion.header
            className="article-page__header"
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
            animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Category Badge */}
            {article.category && (
              <span className="article-page__category">{article.category}</span>
            )}

            {/* Title */}
            <h1 className="article-page__title">{article.title}</h1>

            {/* Intro/Summary */}
            {article.summary && (
              <p className="article-page__intro">{article.summary}</p>
            )}

            {/* Author Strip */}
            <div className="article-page__author-strip">
              {/* Author Info */}
              <div className="article-page__author">
                {article.author?.avatarUrl && (
                  <img
                    src={`${article.author.avatarUrl}?w=80&h=80&fit=fill&q=80`}
                    alt={article.author.name}
                    className="article-page__author-avatar"
                  />
                )}
                <div className="article-page__author-info">
                  <span className="article-page__author-name">
                    {article.author?.name || 'Rosalytics Team'}
                  </span>
                  {article.author?.role && (
                    <span className="article-page__author-role">
                      {article.author.role}
                    </span>
                  )}
                </div>
                {article.author?.linkedIn && (
                  <a
                    href={article.author.linkedIn}
                    className="article-page__author-linkedin"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${article.author.name} on LinkedIn`}
                  >
                    <Linkedin size={16} />
                  </a>
                )}
              </div>

              {/* Meta Info */}
              <div className="article-page__meta">
                <span className="article-page__meta-item">
                  <Calendar size={14} />
                  {formatDate(article.publishedAt)}
                </span>
                {article.readingTime && (
                  <span className="article-page__meta-item">
                    <Clock size={14} />
                    {article.readingTime}
                  </span>
                )}
              </div>
            </div>
          </motion.header>

          {/* Hero Image */}
          {article.heroImage?.url && (
            <motion.figure
              className="article-page__hero"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <img
                src={`${article.heroImage.url}?w=1400&q=85&fm=webp`}
                alt={article.heroImage.alt || article.title}
                className="article-page__hero-image"
              />
              {article.heroImage.alt && (
                <figcaption className="article-page__hero-caption">
                  {article.heroImage.alt}
                </figcaption>
              )}
            </motion.figure>
          )}

          {/* Body Content */}
          <motion.div
            className="article-page__body"
            initial={prefersReducedMotion ? {} : { opacity: 0 }}
            animate={prefersReducedMotion ? {} : { opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <RichTextRenderer content={article.body} />
          </motion.div>

          {/* Tags */}
          {article.tags?.length > 0 && (
            <div className="article-page__tags">
              <span className="article-page__tags-label">Topics:</span>
              <div className="article-page__tags-list">
                {article.tags.map((tag) => (
                  <span key={tag} className="article-page__tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>

        {/* Related Articles */}
        {article.relatedArticles?.length > 0 && (
          <RelatedResources
            items={article.relatedArticles}
            type="article"
            title="Related insights"
          />
        )}

        {/* Footer */}
        <footer className="article-page__footer">
          <div className="article-page__footer-container">
            <p>© 2025 Rosalytics. All rights reserved.</p>
          </div>
        </footer>
      </main>
    </SmoothScroll>
  );
};

export default ArticlePage;
